'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
} from '@ahafood/ui/components/popover';
import type { Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocationMismatchStore } from '@/store/use-location-mismatch-store';

type LocationMismatchPopoverProps = {
  /** Phần tử neo (mục địa chỉ). Popover sẽ trỏ vào đây. */
  children: ReactNode;
  /**
   * Gọi khi user bấm "Thay đổi địa chỉ" — nhận địa chỉ GPS đã reverse-geocode để
   * mở picker với vị trí hiện tại điền sẵn.
   */
  onChangeAddress: (gpsAddress: Address) => void;
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  /**
   * Class làm sáng/nổi ô địa chỉ khi popup mở (thẻ trắng nổi trên lớp phủ, kiểu
   * Grab). Dùng `-mx/-my` để nền mở rộng mà không xô lệch layout xung quanh.
   */
  highlightClassName?: string;
};

const DEFAULT_HIGHLIGHT =
  'rounded-2xl bg-white px-3 py-2 -mx-3 -my-2 shadow-xl ring-1 ring-black/5';

/**
 * "Nhắc nhỏ" đổi địa chỉ — popover gọn (tham khảo Grab): màn hình mờ nhẹ, mục địa
 * chỉ neo được nâng nổi lên trên lớp phủ, mũi tên chỉ vào nó, một dòng nhắc + một
 * nút CTA. Tự bật khi GPS lệch > 500m. Không ép, không chặn: chạm ra ngoài (lớp
 * phủ) để bỏ qua. Tối đa 1 lần/phiên.
 */
export function LocationMismatchPopover({
  children,
  onChangeAddress,
  side = 'bottom',
  align = 'start',
  highlightClassName = DEFAULT_HIGHLIGHT,
}: LocationMismatchPopoverProps) {
  const status = useLocationMismatchStore.use.status();
  const gpsAddress = useLocationMismatchStore.use.gpsAddress();
  const dismiss = useLocationMismatchStore.use.dismiss();

  const open = status === 'mismatch';

  const handleChange = () => {
    if (gpsAddress) {
      onChangeAddress(gpsAddress);
    }
    dismiss();
  };

  // Nâng mục địa chỉ neo nổi lên trên lớp phủ (z-50) để nó vẫn sáng như Grab.
  const anchorChild = isValidElement(children)
    ? cloneElement(children as ReactElement<{ className?: string }>, {
        className: cn(
          (children as ReactElement<{ className?: string }>).props.className,
          open && 'relative z-[60] transition-shadow',
          open && highlightClassName
        ),
      })
    : children;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
    >
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed inset-0 z-50 bg-black/40 animate-in fade-in"
              onClick={() => dismiss()}
              aria-hidden
            />,
            document.body
          )
        : null}
      <PopoverAnchor asChild>{anchorChild}</PopoverAnchor>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={10}
        collisionPadding={12}
        className="z-[70] w-[310px] rounded-2xl border-none p-3.5 shadow-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <PopoverArrow className="fill-white" width={18} height={9} />
        <p className="text-[15px] font-semibold leading-snug text-neutral-90">
          Có vẻ địa chỉ này cách khá xa vị trí hiện tại của bạn.
        </p>
        <div className="mt-1 flex justify-end">
          <Button
            size="sm"
            className="h-9 rounded-full px-4 text-sm font-bold"
            onClick={handleChange}
            type="button"
          >
            Thay đổi địa chỉ
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
